import React from 'react';
import { WithTranslation } from 'react-i18next';
import DateFnsUtils from '@date-io/date-fns';
import frLocale from 'date-fns/locale/fr';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import Icon from '@material-ui/core/Icon';
import InputAdornment from '@material-ui/core/InputAdornment';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';
// import Input from '@material-ui/core/Input';
// import Checkbox from '@material-ui/core/Checkbox';
// import ListItemText from '@material-ui/core/ListItemText';
// Components
import { AutoFill } from '../../components/field-autofill';
// Ressources
import { SortOrder } from '../../redux/reducers/api-types';
import { API_ENDPOINT, TableRequestPoint } from '../../redux/reducers/table/module';
import { HeadCell, FormatType, PickerOption } from '../../local-types';

// With custom style pickerMultiple
// const ITEM_HEIGHT = 48;
// const ITEM_PADDING_TOP = 8;
// const MenuProps = {
//     PaperProps: {
//         style: {
//             maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
//             width: 250,
//         },
//     },
// };

const FONT_SIZE_INPUT = 12; // default 1.0rem = 16

interface EnhancedTableProps extends WithTranslation {
    type: TableRequestPoint;
    filter?: any;
    order: SortOrder;
    orderBy: string;
    uuidTable: string;
    headers: HeadCell[];
    barFilter: boolean; // Activate or not the headers searchBar under collumns name
    onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
}

const EnhancedTableHead = (props: EnhancedTableProps): JSX.Element => {
    const { t, uuidTable, headers, type, barFilter, filter, order, orderBy, onRequestSort } = props;
    const [selectedDate, setSelectedDate] = React.useState<any>({});

    const classes = useStyles();

    let filterKeys = [];
    //TODO move all format into module
    if (type.type === API_ENDPOINT.TICKETING) {
        filterKeys = Object.keys(filter);
    } else if (type.type === API_ENDPOINT.PRICING || type.type === API_ENDPOINT.BILLING) {
        filterKeys = Object.keys(filter).map((k) => /.*\[(.*)\]/g.exec(k)?.[1] || k);
    } else {
        filterKeys = Object.keys(filter).map((k) => /.*\[(.*)\]/g.exec(k)?.[1] || k);
    }
    // console.log('filters', filter, filterKeys);

    const booleanOptions = [
        {
            value: '',
            label: '',
        },
        {
            value: '1',
            label: t('common:true'),
        },
        {
            value: '0',
            label: t('common:false'),
        },
    ];

    const booleanStringOptions = [
        {
            value: '',
            label: '',
        },
        {
            value: 'true',
            label: t('common:true'),
        },
        {
            value: 'false',
            label: t('common:false'),
        },
    ];

    // With custom style pickerMultiple
    // const [multipleValue, setMultipleValue] = React.useState([]);
    // const handleChange = (event) => {
    //     setMultipleValue(event.target.value);
    // };

    const getValueForFilterKey = (key): any => {
        // console.log('Looking for', key, 'in', filterKeys);
        let res = '' as any;
        const indexKey = filterKeys.findIndex((k) => k === key);
        if (indexKey > -1) {
            res = Object.values(filter)[indexKey];
        }
        return res;
    };

    const handleDateChange = (date: Date, id): void => {
        const newState = { ...selectedDate };
        newState[id] = date;
        setSelectedDate(newState);
    };

    const createSortHandler = (property: any) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };

    const renderSearchAdornment = (): JSX.Element => (
        <InputAdornment position="start">
            <SearchIcon style={{ width: '0.6em' }} fontSize="small" />
        </InputAdornment>
    );

    const renderLabel = (label: string): JSX.Element => <div style={{ color: 'rgb(156, 171, 187)' }}>{label}</div>;

    const renderOrderedBy = (orderBy, id): JSX.Element => {
        if (orderBy === id) {
            return (
                <span className={classes.visuallyHidden}>
                    {order === SortOrder.DESC ? 'sorted descending' : 'sorted ascending'}
                </span>
            );
        }
        return null;
    };

    const renderSortLabel = (sortable: boolean, label: string, id: string, orderBy: string): JSX.Element => {
        if (sortable === false) {
            return renderLabel(label);
        } else {
            return (
                <TableSortLabel active={orderBy === id} direction={order} onClick={createSortHandler(id)}>
                    {renderLabel(label)}
                    {renderOrderedBy(orderBy, id)}
                </TableSortLabel>
            );
        }
    };

    const renderFilterableCells = (): JSX.Element[] => {
        let Cell: JSX.Element = null;

        // If no filterable return null
        if (!headers.find((h) => h.filterable !== false)) {
            return null;
        }

        return headers.map(
            ({
                filterable,
                pickerOptions,
                type,
                label,
                placeholder,
                id,
                idSearch,
                api,
                customAlt,
                isUuid,
                minLength,
            }) => {
                if (filterable === false) {
                    Cell = <></>;
                } else {
                    let options = booleanOptions as PickerOption[];
                    if (pickerOptions) {
                        options = pickerOptions;
                    } else if (type === FormatType.BooleanString) {
                        options = booleanStringOptions as PickerOption[];
                    }
                    const defaultValue = getValueForFilterKey(id);

                    switch (type) {
                        case FormatType.DateLong:
                        case FormatType.DateShort:
                            Cell = (
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
                                    <KeyboardDatePicker
                                        disableToolbar
                                        variant="inline"
                                        format="dd/MM/yyyy"
                                        margin="normal"
                                        id={`date-picker-inline-${id}-${uuidTable}`}
                                        label={placeholder ?? label}
                                        name={idSearch ?? id}
                                        value={selectedDate[id] || defaultValue || null}
                                        onChange={(date) => handleDateChange(date, id)}
                                        inputProps={{ form: uuidTable }}
                                        InputProps={{ startAdornment: renderSearchAdornment() }}
                                        KeyboardButtonProps={{ 'aria-label': 'change date' }}
                                    />
                                </MuiPickersUtilsProvider>
                            );
                            break;
                        case FormatType.Boolean:
                        case FormatType.BooleanString:
                        case FormatType.Picker:
                            const native = options.find((o) => o.icon) != null;
                            Cell = (
                                <TextField
                                    id={`standard-select-picker-native-${id}-${uuidTable}`}
                                    select
                                    label={placeholder ?? label}
                                    name={idSearch ?? id}
                                    defaultValue={Array.isArray(defaultValue) ? defaultValue[0] : defaultValue}
                                    className={classes.textField}
                                    inputProps={{ form: uuidTable }}
                                    SelectProps={{
                                        native: !native,
                                        MenuProps: {
                                            className: classes.menu,
                                        },
                                    }}
                                    margin="normal"
                                >
                                    {options.map((option) =>
                                        native ? (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.icon && (
                                                    <ListItemIcon style={{ minWidth: 26 }}>
                                                        <Icon fontSize="small">{option.icon}</Icon>
                                                    </ListItemIcon>
                                                )}
                                                <Typography variant="inherit" noWrap style={{ minHeight: 20 }}>
                                                    {option.label}
                                                </Typography>
                                            </MenuItem>
                                        ) : (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ),
                                    )}
                                </TextField>
                            );
                            break;
                        case FormatType.PickerMultiple:
                            Cell = (
                                <Select
                                    multiple
                                    native
                                    label={placeholder ?? label}
                                    name={idSearch ?? id}
                                    // A multiple select only accepts an array, but a filter
                                    // restored from the url holds a string when it has one value
                                    defaultValue={
                                        Array.isArray(defaultValue) ? defaultValue : defaultValue ? [defaultValue] : []
                                    }
                                    className={classes.pickerMultiple}
                                    inputProps={{
                                        id: `select-multiple-native-${id}-${uuidTable}`,
                                        form: uuidTable,
                                    }}
                                >
                                    {options.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                            );
                            break;
                        // Custom style
                        // case FormatType.PickerMultiple:
                        //     Cell = (
                        //         <Select
                        //             labelId="mutiple-checkbox-label-${id}"
                        //             id="mutiple-checkbox-label-${id}"
                        //             multiple
                        //             value={multipleValue}
                        //             onChange={handleChange}
                        //             input={<Input inputProps={{ form: uuidTable }} />}
                        //             renderValue={(selected: Array<string>) => selected.join(', ')}
                        //             MenuProps={MenuProps}
                        //         >
                        //             {options.map(({ value, label }) => (
                        //                 <MenuItem key={value} value={value}>
                        //                     <Checkbox checked={multipleValue.indexOf(value) > -1} />
                        //                     <ListItemText primary={label} />
                        //                 </MenuItem>
                        //             ))}
                        //         </Select>
                        //     );
                        //     break;
                        case FormatType.Autocomplete:
                            if (!api) {
                                return <div>NO API Given</div>;
                            } else {
                                // const defaultNameValue = getValueForFilterKey(`${id}_name`);
                                Cell = (
                                    <AutoFill
                                        shortLabel={true}
                                        datas={{
                                            id: idSearch ?? id,
                                            label: placeholder ?? label,
                                            minChar: 1,
                                            style: { width: '100%', marginBottom: 0, marginLeft: 0 },
                                            required: false,
                                            inputParams: { form: uuidTable, style: { fontSize: FONT_SIZE_INPUT } },
                                            isUuid: isUuid,
                                            customAlt: customAlt,
                                            defaultValue: { id: defaultValue, name: defaultValue } as any, // TODO: use defaultNameValue but sending id is hard
                                            loaderPosition: 'right',
                                            loaderProps: { center: true, size: 24 },
                                            api: api,
                                        }}
                                    />
                                );
                            }
                            break;
                        default:
                            const fieldType =
                                type === FormatType.Price || type === FormatType.Number ? 'number' : 'text';
                            const inputProps: any = {
                                form: uuidTable,
                                placeholder: '',
                                style: { fontSize: FONT_SIZE_INPUT },
                            };
                            if (type === FormatType.Price || type === FormatType.Number) {
                                inputProps.step = 0.01;
                            }
                            if (type === FormatType.Phone) {
                                inputProps.placeholder = t('common:form.placeholder.phone');
                            }
                            if (minLength) {
                                inputProps.minLength = minLength;
                            }
                            Cell = (
                                <TextField
                                    label={placeholder ?? label}
                                    name={idSearch ?? id}
                                    type={fieldType}
                                    defaultValue={defaultValue}
                                    className={classes.flex}
                                    inputProps={inputProps}
                                    InputProps={{ startAdornment: renderSearchAdornment() }}
                                />
                            );
                    }
                }

                return (
                    <TableCell key={id + 'search'} style={{ flex: 1 }}>
                        {Cell}
                    </TableCell>
                );
            },
        );
    };

    const renderSortableCells = (): JSX.Element[] => {
        return headers.map(({ id, idSort, numeric, label, sortable }) => {
            const key = idSort ?? id; // Select the idSort when available and the id otherwise as the sort key
            return (
                <TableCell
                    key={key}
                    align={numeric ? 'right' : 'left'}
                    padding="normal"
                    sortDirection={orderBy === key ? order : false}
                >
                    {renderSortLabel(sortable, label, key, orderBy)}
                </TableCell>
            );
        });
    };

    return (
        <TableHead>
            <TableRow>{renderSortableCells()}</TableRow>
            {barFilter && <TableRow>{renderFilterableCells()}</TableRow>}
        </TableHead>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        visuallyHidden: {
            border: 0,
            clip: 'rect(0 0 0 0)',
            height: 1,
            margin: -1,
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            top: 20,
            width: 1,
        },
        flex: {
            display: 'flex',
        },
        textField: {
            display: 'flex',
            minWidth: 75,
            marginTop: theme.spacing(1),
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
        },
        menu: {
            width: 200,
        },
        pickerMultiple: {
            '& .MuiSelect-select[multiple]': {
                width: 'auto',
            },
        },
    }),
);

export default React.memo(EnhancedTableHead);
